<template>
  <IntercomLayout>
    <template #topbar>
      <IntercomTopbar
        app-name="OnePlaceHQ"
        product-name="Billing"
        :business-name="businessName"
        :user="user"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="invoice-detail-view">
      <!-- Back link -->
      <button class="back-link" @click="router.push('/invoices')">
        &larr; Back to Invoices
      </button>

      <div v-if="isLoading" class="loading-state">Loading...</div>

      <div v-else-if="invoice" class="invoice-content">
        <!-- Header -->
        <div class="page-header">
          <div>
            <h1 class="page-title">{{ invoice.invoice_number }}</h1>
            <p class="page-subtitle">{{ invoice.customer_name || 'Walk-in Customer' }}</p>
          </div>
          <div class="header-actions">
            <IntercomBadge :variant="getStatusVariant(invoice.status)">
              {{ capitalize(invoice.status) }}
            </IntercomBadge>
            <IntercomBadge :variant="getPaymentVariant(invoice.payment_status)">
              {{ capitalize(invoice.payment_status) }}
            </IntercomBadge>
          </div>
        </div>

        <!-- Invoice Info -->
        <IntercomCard>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Invoice Date</span>
              <span class="info-value">{{ formatDate(invoice.invoice_date) }}</span>
            </div>
            <div v-if="invoice.due_date" class="info-item">
              <span class="info-label">Due Date</span>
              <span class="info-value">{{ formatDate(invoice.due_date) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Invoice Type</span>
              <span class="info-value">{{ invoice.invoice_type === 'tax_invoice' ? 'Tax Invoice' : 'Cash Memo' }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Total Amount</span>
              <span class="info-value highlight">{{ formatCurrency(invoice.total_amount) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Amount Paid</span>
              <span class="info-value">{{ formatCurrency(invoice.amount_paid) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Amount Due</span>
              <span class="info-value" :class="{ 'text-danger': invoice.amount_due > 0 }">
                {{ formatCurrency(invoice.amount_due) }}
              </span>
            </div>
          </div>
        </IntercomCard>

        <!-- Line Items -->
        <IntercomCard>
          <h3 class="section-heading">Line Items</h3>
          <IntercomTable :columns="itemColumns" :data="invoice.items || []">
            <template #cell-quantity="{ value }">
              {{ parseFloat(Number(value).toFixed(3)) }}
            </template>
            <template #cell-unit_price="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-discount_display="{ row }">
              <template v-if="row.discount_amount > 0">
                <span v-if="row.discount_type === 'percentage'">{{ row.discount_value }}%</span>
                <span v-else>{{ formatCurrency(row.discount_amount) }}</span>
              </template>
              <span v-else class="text-muted">-</span>
            </template>
            <template #cell-taxable_amount="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-tax_amount="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-line_total="{ value }">
              <strong>{{ formatCurrency(value) }}</strong>
            </template>
          </IntercomTable>
        </IntercomCard>

        <!-- Tax Summary -->
        <IntercomCard v-if="invoice.total_tax_amount > 0">
          <h3 class="section-heading">Tax Summary</h3>
          <div class="tax-grid">
            <div class="tax-item">
              <span class="info-label">Subtotal</span>
              <span class="info-value">{{ formatCurrency(invoice.subtotal) }}</span>
            </div>
            <div v-if="invoice.cgst_amount > 0" class="tax-item">
              <span class="info-label">CGST</span>
              <span class="info-value cgst">{{ formatCurrency(invoice.cgst_amount) }}</span>
            </div>
            <div v-if="invoice.sgst_amount > 0" class="tax-item">
              <span class="info-label">SGST</span>
              <span class="info-value sgst">{{ formatCurrency(invoice.sgst_amount) }}</span>
            </div>
            <div v-if="invoice.igst_amount > 0" class="tax-item">
              <span class="info-label">IGST</span>
              <span class="info-value igst">{{ formatCurrency(invoice.igst_amount) }}</span>
            </div>
            <div v-if="invoice.discount_amount > 0" class="tax-item">
              <span class="info-label">Discount</span>
              <span class="info-value">-{{ formatCurrency(invoice.discount_amount) }}</span>
            </div>
            <div class="tax-item total">
              <span class="info-label">Grand Total</span>
              <span class="info-value">{{ formatCurrency(invoice.total_amount) }}</span>
            </div>
          </div>
        </IntercomCard>

        <!-- Payments -->
        <IntercomCard v-if="invoice.payments && invoice.payments.length > 0">
          <h3 class="section-heading">Payments</h3>
          <IntercomTable :columns="paymentColumns" :data="invoice.payments">
            <template #cell-amount="{ value }">
              {{ formatCurrency(value) }}
            </template>
            <template #cell-payment_date="{ value }">
              {{ formatDate(value) }}
            </template>
            <template #cell-payment_mode="{ value }">
              {{ capitalize(value) }}
            </template>
          </IntercomTable>
        </IntercomCard>

        <!-- Actions -->
        <div class="action-bar">
          <IntercomButton
            v-if="invoice.status === 'draft'"
            variant="primary"
            @click="handleFinalize"
          >
            Finalize Invoice
          </IntercomButton>
          <IntercomButton
            v-if="!invoice.voided_at"
            variant="secondary"
            @click="showEditModal = true"
          >
            Edit
          </IntercomButton>
          <IntercomButton
            v-if="invoice.status !== 'draft' && !invoice.voided_at"
            variant="danger"
            @click="handleVoid"
          >
            Void Invoice
          </IntercomButton>
          <IntercomButton
            v-if="invoice.amount_due > 0 && invoice.status === 'finalized'"
            variant="primary"
            @click="showPaymentModal = true"
          >
            Record Payment
          </IntercomButton>
          <IntercomButton
            variant="secondary"
            @click="handlePrint"
          >
            Print
          </IntercomButton>
        </div>
      </div>

      <div v-else class="empty-state">Invoice not found</div>

      <!-- Payment Modal -->
      <PaymentForm
        v-model:show="showPaymentModal"
        :invoice="invoice"
        @success="handlePaymentSuccess"
      />

      <!-- Edit Invoice Modal -->
      <InvoiceForm
        v-if="invoice"
        v-model:show="showEditModal"
        :invoice="invoice"
        @success="handleEditSuccess"
      />
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuth } from '@shared/composables/useAuth.js'
import { useConfirm } from '@shared/composables/useConfirm.js'
import { useToast } from '@shared/composables/useToast.js'
import { useBilling } from '../composables/useBilling.js'
import {
  IntercomLayout,
  IntercomTopbar,
  IntercomSidebar,
  IntercomCard,
  IntercomTable,
  IntercomButton,
  IntercomBadge,
} from '@shared/components/intercom'
import PaymentForm from '../components/PaymentForm.vue'
import InvoiceForm from '../components/InvoiceForm.vue'
import { billingNavSections } from '../config/navigation.js'
import { formatDate } from '../utils/billingUtils.js'

const route = useRoute()
const router = useRouter()
const { user, logout } = useAuth()
const { confirm } = useConfirm()
const toast = useToast()
const { getInvoice, finalizeInvoice, voidInvoice, businessName, fetchBusinessName } = useBilling()

const navSections = billingNavSections

const invoice = ref(null)
const isLoading = ref(true)
const showPaymentModal = ref(false)
const showEditModal = ref(false)

const itemColumns = [
  { key: '_sno', title: '#', width: '40px', align: 'center' },
  { key: 'product_name', title: 'Product', width: '200px' },
  { key: 'hsn_code', title: 'HSN', width: '100px' },
  { key: 'quantity', title: 'Qty', align: 'right', width: '80px' },
  { key: 'unit_price', title: 'Unit Price', align: 'right', width: '110px' },
  { key: 'discount_display', title: 'Discount', align: 'right', width: '90px' },
  { key: 'taxable_amount', title: 'Taxable', align: 'right', width: '110px' },
  { key: 'tax_amount', title: 'Tax', align: 'right', width: '90px' },
  { key: 'line_total', title: 'Total', align: 'right', width: '110px' },
]

const paymentColumns = [
  { key: 'payment_date', title: 'Date', width: '130px' },
  { key: 'payment_mode', title: 'Mode', width: '120px' },
  { key: 'amount', title: 'Amount', align: 'right', width: '130px' },
  { key: 'reference_number', title: 'Reference', width: '150px' },
]

const capitalize = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : ''

const formatCurrency = (val) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(val || 0)

const getStatusVariant = (s) => {
  if (s === 'finalized') return 'success'
  if (s === 'cancelled' || s === 'voided') return 'error'
  return 'default'
}

const getPaymentVariant = (s) => {
  if (s === 'paid') return 'success'
  if (s === 'partial') return 'warning'
  return 'default'
}

const fetchInvoiceDetail = async () => {
  isLoading.value = true
  try {
    const response = await getInvoice(route.params.id)
    const inv = response.invoice || response
    if (inv.items) {
      inv.items = inv.items.map((item, i) => ({ ...item, _sno: i + 1 }))
    }
    invoice.value = inv
  } catch (error) {
    console.error('Failed to fetch invoice:', error)
    invoice.value = null
  } finally {
    isLoading.value = false
  }
}

const handleFinalize = async () => {
  const ok = await confirm(`Finalize invoice "${invoice.value.invoice_number}"? This cannot be undone.`, { title: 'Finalize Invoice' })
  if (!ok) return
  try {
    const finalizeRes = await finalizeInvoice(invoice.value.id)
    const warnings = finalizeRes.low_stock_warnings || finalizeRes.data?.low_stock_warnings
    if (warnings && warnings.length > 0) {
      const names = warnings.map(w => `${w.product_name} (${w.stock_quantity} left)`).join(', ')
      toast.warning(`Low stock: ${names}`, { duration: 6000 })
    }
    await fetchInvoiceDetail()
  } catch (error) {
    console.error('Failed to finalize:', error)
  }
}

const handlePrint = () => {
  router.push(`/invoices?print=${invoice.value.id}`)
}

const handleVoid = async () => {
  const ok = await confirm(
    `Void invoice "${invoice.value.invoice_number}"? This will reverse stock and loyalty changes. This cannot be undone.`,
    { title: 'Void Invoice' }
  )
  if (!ok) return
  try {
    await voidInvoice(invoice.value.id, { reason: 'Voided by user' })
    toast.success('Invoice voided')
    await fetchInvoiceDetail()
  } catch (error) {
    console.error('Failed to void invoice:', error)
    toast.error('Failed to void: ' + (error.message || 'Unknown error'))
  }
}

const handleEditSuccess = async () => {
  showEditModal.value = false
  await fetchInvoiceDetail()
}

const handlePaymentSuccess = async () => {
  showPaymentModal.value = false
  await fetchInvoiceDetail()
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusinessName()
  fetchInvoiceDetail()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.invoice-detail-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) 0;
  margin-bottom: var(--space-4);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-blue);
  background: none;
  border: none;
  cursor: pointer;
}

.back-link:hover {
  text-decoration: underline;
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: var(--space-6);
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin: var(--space-1) 0 0;
}

.header-actions {
  display: flex;
  gap: var(--space-2);
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: var(--space-4);
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.info-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
  color: var(--color-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
}

.info-value.highlight {
  color: var(--color-blue);
  font-size: var(--font-size-lg);
}

.info-value.cgst { color: #2563eb; }
.info-value.sgst { color: #7c3aed; }
.info-value.igst { color: #0891b2; }

.text-danger {
  color: #dc2626;
}

.section-heading {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-charcoal);
  margin: 0 0 var(--space-4);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.tax-grid {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  max-width: 350px;
}

.tax-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.tax-item.total {
  padding-top: var(--space-3);
  border-top: 2px solid var(--color-gray-300);
}

.tax-item.total .info-value {
  font-size: var(--font-size-lg);
  color: var(--color-blue);
  font-weight: var(--font-weight-bold);
}

.action-bar {
  display: flex;
  gap: var(--space-3);
  margin-top: var(--space-6);
}

.loading-state,
.empty-state {
  text-align: center;
  padding: var(--space-10);
  color: var(--color-gray-500);
  font-size: var(--font-size-sm);
}

.invoice-content {
  display: flex;
  flex-direction: column;
  gap: var(--space-6);
}

@media (max-width: 768px) {
  .invoice-detail-view { padding: var(--space-4); }
  .page-header { flex-direction: column; gap: var(--space-3); }
  .info-grid { grid-template-columns: repeat(2, 1fr); }
}
</style>
